import { keepPreviousData } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { SortingState } from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import { useGetEquityExploreTable } from '#/api/generated/explore-table/explore-table';
import { DataTable } from '#/components/data/DataTable';
import { DataTableSkeleton } from '#/components/data/DataTableSkeleton';
import { DEFAULT_SORTING } from '#/components/index/EquitiesExploreTable/constants';
import type { EquityColumnId } from '#/components/index/EquitiesExploreTable/data';
import {
  equitiesColumns,
  equityRowId,
  getApiSortKey,
  getSortColumn,
  getSortDirection,
} from '#/components/index/EquitiesExploreTable/data';
import { useIsBelowFoldInView } from '#/components/index/HomepageBelowFoldSection';
import { getSearchSortParams } from '#/components/search/searchPagination';
import { Icon } from '#/components/ui/icons';
import { QueryBoundary } from '#/components/ui/QueryBoundary';
import type { ReturnsTimeframeId } from '#/components/ui/returnsTimeframe';
import {
  getReturnsTimeframeFromSorting,
  syncSortingToReturnsTimeframe,
} from '#/components/ui/returnsTimeframe';
import { ReturnsTimeframeFilter } from '#/components/ui/ReturnsTimeframeFilter';
import { Spinner } from '#/components/ui/Spinner';
import type { SectorSlug } from '#/types';
import { cn } from '#/utils/cn';

const EQUITY_RETURN_COLUMN_IDS = {
  oneYear: 'trailing_1yr_return',
  fiveYear: 'trailing_5yr_return',
} as const;

export function EquitiesExploreTable({
  category,
  children,
  limit,
  title = 'Equities',
  id = 'explore-equities',
  visibleColumns,
  initialSorting = DEFAULT_SORTING,
  compact = false,
  tickerTrack,
}: {
  category?: SectorSlug;
  children?: React.ReactNode;
  limit?: number;
  title?: string;
  id?: string;
  visibleColumns?: EquityColumnId[];
  initialSorting?: SortingState;
  compact?: boolean;
  /**
   * Overrides the ticker link's analytics annotation. Omitted keeps the
   * factory default (`explore_table_ticker` / `equities_explore`) so the home
   * page is unchanged; the category index passes `explore_table` context.
   */
  tickerTrack?: { track: string; context: string };
}) {
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const [returnsTimeframe, setReturnsTimeframe] = useState<ReturnsTimeframeId>(
    () => getReturnsTimeframeFromSorting(sorting, EQUITY_RETURN_COLUMN_IDS),
  );
  const activeSortColumn = getSortColumn(sorting, 'price');
  const searchSortParams = getSearchSortParams('equity', sorting);
  const isInView = useIsBelowFoldInView();

  const query = useGetEquityExploreTable(
    {
      limit,
      order_by: getSortDirection(sorting),
      sector_slug: category,
      sort_by: getApiSortKey(activeSortColumn),
    },
    {
      query: {
        enabled: isInView,
        placeholderData: keepPreviousData,
      },
    },
  );

  const columns = useMemo(
    () =>
      equitiesColumns(
        'Equity',
        getEquityVisibleColumns(visibleColumns, returnsTimeframe),
        { compact, tickerTrack },
      ),
    [compact, returnsTimeframe, visibleColumns, tickerTrack],
  );
  const showReturnsTimeframeFilter = hasEquityReturnsColumn(visibleColumns);
  const displayTitle = getEquityDisplayTitle(title, returnsTimeframe);

  function handleReturnsTimeframeChange(nextTimeframe: ReturnsTimeframeId) {
    setReturnsTimeframe(nextTimeframe);
    setSorting((currentSorting) =>
      syncSortingToReturnsTimeframe(
        currentSorting,
        nextTimeframe,
        EQUITY_RETURN_COLUMN_IDS,
      ),
    );
  }

  return (
    <section
      id={id}
      className="flow flow-inner-8 min-w-0 scroll-mt-(--scroll-offset)"
    >
      {children}

      <div className="flow-24 p-gap border-divider-subtle -mx-gap min-h-full border-b sm:mx-0 sm:rounded sm:border">
        <QueryBoundary
          query={query}
          loading={
            <DataTableSkeleton
              columns={columns.length}
              rows={limit ?? 10}
              title={title}
              compact={compact}
              label={`Loading ${title.toLowerCase()}…`}
            />
          }
        >
          {(response) => {
            const rows = response.data.results ?? [];

            return (
              <>
                <div
                  className={cn(
                    'flex flex-col items-start gap-8 sm:flex-row sm:flex-wrap sm:justify-between sm:gap-x-12',
                    compact ? 'mb-12' : 'mb-16',
                  )}
                >
                  <h3 className="text-fluid-base text-fg-header min-w-0 flex-1 font-semibold">
                    <span id={`${id}-title`} className="text-balance">
                      {displayTitle}
                    </span>
                    <span
                      aria-hidden={!query.isFetching}
                      className={cn(
                        'text-fg-muted pointer-events-none ml-8 inline-flex align-middle transition-opacity duration-150',
                        query.isFetching
                          ? 'opacity-100 delay-20'
                          : 'invisible opacity-0 delay-0',
                      )}
                    >
                      <Spinner
                        label={`Refreshing ${displayTitle.toLowerCase()}…`}
                        className="w-auto py-0"
                        size={16}
                      />
                    </span>
                  </h3>

                  <div
                    className={cn(
                      'w-full sm:w-auto',
                      compact && showReturnsTimeframeFilter
                        ? 'flex flex-col items-start gap-12 sm:items-end'
                        : '',
                    )}
                  >
                    <Link
                      to="/search"
                      search={{
                        query: '',
                        investment_type: 'equity',
                        sort_by: searchSortParams.sort_by,
                        order_by: searchSortParams.order_by,
                        equity_sector_slug: category ? [category] : undefined,
                        equity_industry_slug: undefined,
                        market_cap_categories: undefined,
                        fund_category_slug: undefined,
                        equity_next_pagination_token: undefined,
                        equity_prev_pagination_token: undefined,
                        etf_next_pagination_token: undefined,
                        etf_prev_pagination_token: undefined,
                        mutual_fund_next_pagination_token: undefined,
                        mutual_fund_prev_pagination_token: undefined,
                        listing_type: undefined,
                        exchange: undefined,
                      }}
                      id={`${id}-view-more`}
                      aria-labelledby={`${id}-view-more ${id}-title`}
                      className="mdc-btn mdc-btn--md mdc-btn--link -my-10 self-start"
                    >
                      Filter and View More
                      <Icon
                        id="arrow-right"
                        size={16}
                        className="ml-4 inline-block"
                      />
                    </Link>

                    {compact && showReturnsTimeframeFilter ? (
                      <ReturnsTimeframeFilter
                        timeframe={returnsTimeframe}
                        onChange={handleReturnsTimeframeChange}
                        compact
                        trackContext="equities_explore"
                      />
                    ) : null}
                  </div>
                </div>

                {showReturnsTimeframeFilter && !compact ? (
                  <div className="flex justify-end">
                    <ReturnsTimeframeFilter
                      timeframe={returnsTimeframe}
                      onChange={handleReturnsTimeframeChange}
                      trackContext="equities_explore"
                    />
                  </div>
                ) : null}

                <div className={cn('relative', compact ? 'mt-12' : 'mt-16')}>
                  <DataTable
                    aria-label={`${displayTitle} table`}
                    className={cn(
                      '-mx-gap transition-opacity sm:mx-0',
                      query.isFetching && 'opacity-60',
                    )}
                    columns={columns}
                    data={rows}
                    sorting={sorting}
                    onSortingChange={setSorting}
                    manualSorting
                    getRowId={equityRowId}
                    pageSize={10}
                    compact={compact}
                    emptyState="No equities on file"
                    trackContext="equities_explore"
                  />
                  <div
                    aria-hidden={!query.isFetching}
                    className={cn(
                      'pointer-events-none absolute inset-0 z-10 flex items-center justify-center transition-opacity',
                      query.isFetching ? 'opacity-100' : 'invisible opacity-0',
                    )}
                  >
                    <Spinner
                      label={`Refreshing ${displayTitle.toLowerCase()} table…`}
                    />
                  </div>
                </div>
              </>
            );
          }}
        </QueryBoundary>
      </div>
    </section>
  );
}

function getEquityVisibleColumns(
  visibleColumns: EquityColumnId[] | undefined,
  returnsTimeframe: ReturnsTimeframeId,
): EquityColumnId[] {
  const returnColumnId =
    returnsTimeframe === '1Y'
      ? EQUITY_RETURN_COLUMN_IDS.oneYear
      : EQUITY_RETURN_COLUMN_IDS.fiveYear;

  return (
    visibleColumns ?? ['ticker', 'price', 'trailing_5yr_return', 'market_cap']
  ).map((columnId) =>
    columnId === EQUITY_RETURN_COLUMN_IDS.oneYear ||
    columnId === EQUITY_RETURN_COLUMN_IDS.fiveYear
      ? returnColumnId
      : columnId,
  );
}

function hasEquityReturnsColumn(visibleColumns: EquityColumnId[] | undefined) {
  return getEquityVisibleColumns(visibleColumns, '5Y').some(
    (columnId) =>
      columnId === EQUITY_RETURN_COLUMN_IDS.oneYear ||
      columnId === EQUITY_RETURN_COLUMN_IDS.fiveYear,
  );
}

function getEquityDisplayTitle(
  title: string,
  returnsTimeframe: ReturnsTimeframeId,
) {
  const timeframeLabel = returnsTimeframe === '1Y' ? '1-Year' : '5-Year';

  return title.replace(/(?:1|5)-Year/, timeframeLabel);
}
